// 统计报表 - 复刻 iOS app 报表 tab
// 4 区域：顾客分析（本月）/ 悠巡（近 7 天）/ 巡店通（今日）/ 事件统计（近 7 天）
//
// 接口（反编译 HomePageManager + 实测可用）：
//   /statistic/total/summary   (standard) — 顾客分析 4 卡片，含环比
//   /statistic/summary/youxun  (standard) — 悠巡 AI 检测模型列表
//   /statistic/summary/xundiantong (statgin) — 巡店通 9 个数字（PascalCase: StartDate/EndDate）
//   /statistic/summary/event   (standard) — 事件统计（服务端 SQL bug 时返回 SQL 错误）
import { Button, Checkbox, Drawer, Empty, Input, message as antdMessage, Spin, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
	UserOutlined,
	SafetyOutlined,
	ShopOutlined,
	BellOutlined,
	BarChartOutlined,
	ArrowDownOutlined,
	ArrowUpOutlined,
	HolderOutlined,
	MinusCircleOutlined,
	PlusCircleOutlined,
	SettingOutlined,
} from '@ant-design/icons';
import { callInternal } from '../api/internal.js';
import {
	fetchHomeAppModule,
	fetchTotalSummary,
	saveHomeAppModule,
	type HomeAppSection,
	type TotalSummaryItem,
} from '../api/statistic.js';
import { formatDateRange, formatNumber, formatShortDate, marketCurrency } from '../utils/locale-format.js';
import { capabilitiesForArea } from '../capabilities.js';
import { useRegion } from '../hooks/useRegion.js';
import {
	ActionTileGrid,
	MobileRootPage,
	RegionTag,
	RootHeader,
	SectionBlock,
	SummaryStrip,
} from '../components/MobileRootScaffold.js';

const PRIMARY = '#FF7043';

const ymd = (d: Date) =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => ymd(new Date());

function thisMonth(): { start: string; end: string } {
	const d = new Date();
	const start = new Date(d.getFullYear(), d.getMonth(), 1);
	return { start: ymd(start), end: ymd(d) };
}
/** 上月同期段：4/1 ~ 同 day-of-month（iOS 风格，匹配本月 1 日至今的窗口） */
function lastMonthSameWindow(): { start: string; end: string } {
	const d = new Date();
	const start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
	const end = new Date(d.getFullYear(), d.getMonth() - 1, d.getDate());
	return { start: ymd(start), end: ymd(end) };
}
function last7Days(): { start: string; end: string } {
	const d = new Date();
	const start = new Date(d);
	start.setDate(start.getDate() - 6);
	return { start: ymd(start), end: ymd(d) };
}

type SectionKey = 'cust' | 'youxun' | 'xdt' | 'event';
const ALL_SECTIONS: SectionKey[] = ['cust', 'youxun', 'xdt', 'event'];
const STORAGE_KEY = 'ulucu-reports-sections';

const SECTION_RENDER: Record<SectionKey, React.ComponentType> = {
	cust: CustomerSection,
	youxun: YouxunSection,
	xdt: XundiantongSection,
	event: EventSection,
};

function loadSections(): SectionKey[] {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return ALL_SECTIONS;
		const arr = JSON.parse(raw) as SectionKey[];
		const valid = arr.filter((k) => ALL_SECTIONS.includes(k));
		return valid.length > 0 ? valid : ALL_SECTIONS;
	} catch {
		return ALL_SECTIONS;
	}
}

export default function ReportsTab() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const region = useRegion();
	const [sections, setSections] = useState<SectionKey[]>(loadSections);
	const [editorOpen, setEditorOpen] = useState(false);
	const analyticsCaps = useMemo(
		() => capabilitiesForArea('analytics', region, ['available']).filter((cap) => cap.moduleKey),
		[region],
	);

	const onSave = (next: SectionKey[]) => {
		setSections(next);
		localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
		setEditorOpen(false);
	};

	return (
		<MobileRootPage>
			<RootHeader
				icon={<BarChartOutlined />}
				title={t('tabbar.analytics')}
				subtitle={t('root.analytics.subtitle')}
				meta={<RegionTag region={region} />}
			/>

			<SummaryStrip
				items={[
					{ key: 'modules', label: t('root.analytics.visible_modules'), value: sections.length },
					{ key: 'period', label: t('root.analytics.primary_period'), value: t('rep.cust.this_month') },
					{ key: 'trend', label: t('root.analytics.trend_period'), value: t('rep.last_7d') },
					{ key: 'currency', label: t('root.analytics.currency'), value: marketCurrency(i18n.language) },
				]}
			/>

			<SectionBlock title={t('root.analytics.tools')}>
				<ActionTileGrid
					items={analyticsCaps.map((cap) => ({
						key: cap.key,
						title: t(cap.labelKey),
						subtitle: t('capability.available_short'),
						icon: cap.key === 'traffic' ? <ShopOutlined /> : <UserOutlined />,
						onClick: () => navigate(cap.route),
					}))}
				/>
			</SectionBlock>

			<SectionBlock
				title={t('root.analytics.insights')}
				extra={
					<Button
						size="small"
						onClick={() => setEditorOpen(true)}
						style={{ borderRadius: 8, color: PRIMARY, borderColor: '#ffd7c8' }}
					>
						<HolderOutlined /> {t('rep.editor.btn')}
					</Button>
				}
			>
				<div style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fill, minmax(min(420px, 100%), 1fr))',
					gap: 12,
				}}>
					{sections.map((key) => {
						const Comp = SECTION_RENDER[key];
						return <Comp key={key} />;
					})}
				</div>
			</SectionBlock>

			<SectionEditor
				open={editorOpen}
				current={sections}
				onClose={() => setEditorOpen(false)}
				onSave={onSave}
			/>
		</MobileRootPage>
	);
}

/* ========== 排序与添加 Drawer ========== */

function SectionEditor({
	open,
	current,
	onClose,
	onSave,
}: {
	open: boolean;
	current: SectionKey[];
	onClose: () => void;
	onSave: (next: SectionKey[]) => void;
}) {
	const { t } = useTranslation();
	const [draft, setDraft] = useState<SectionKey[]>(current);
	const [dragIdx, setDragIdx] = useState<number | null>(null);

	useEffect(() => { if (open) setDraft(current); }, [open, current]);

	const remove = (key: SectionKey) => setDraft((p) => p.filter((k) => k !== key));
	const add = (key: SectionKey) => setDraft((p) => p.includes(key) ? p : [...p, key]);
	const onDrop = (toIdx: number) => {
		if (dragIdx === null || dragIdx === toIdx) return;
		setDraft((p) => {
			const next = [...p];
			const [moved] = next.splice(dragIdx, 1);
			next.splice(toIdx, 0, moved);
			return next;
		});
		setDragIdx(null);
	};

	const draftSet = new Set(draft);
	const candidates = ALL_SECTIONS.filter((k) => !draftSet.has(k));

	const titleOf = (k: SectionKey) => t(`rep.${k}.title`);

	return (
		<Drawer
			placement="bottom"
			height="80%"
			open={open}
			onClose={onClose}
			closable={false}
			title={
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Button type="text" size="small" onClick={onClose} style={{ color: '#666' }}>
						{t('rep.editor.cancel')}
					</Button>
					<span style={{ flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 500 }}>
						{t('rep.editor.title')}
					</span>
					<Button
						type="primary"
						size="small"
						onClick={() => onSave(draft)}
						style={{ background: PRIMARY, borderColor: PRIMARY, fontSize: 14 }}
					>
						{t('rep.editor.ok')}
					</Button>
				</div>
			}
			styles={{ body: { padding: 0, background: '#fafafa' }, header: { padding: '8px 16px' } }}
		>
			<div style={{
				display: 'flex',
				alignItems: 'center',
				padding: '10px 14px',
				background: '#f0f0f0',
				color: '#888',
				fontSize: 12,
			}}>
				<span style={{ flex: 1 }}>{t('rep.editor.shown')}</span>
				<span style={{ display: 'flex', alignItems: 'center', gap: 4, color: PRIMARY }}>
					<SettingOutlined />
					{t('rep.editor.advanced')}
				</span>
			</div>

			{draft.map((key, idx) => (
				<div
					key={key}
					draggable
					onDragStart={() => setDragIdx(idx)}
					onDragOver={(e) => e.preventDefault()}
					onDrop={() => onDrop(idx)}
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 14,
						padding: '14px 16px',
						background: '#fff',
						borderBottom: '1px solid #f5f5f5',
						cursor: 'move',
					}}
				>
					<MinusCircleOutlined
						onClick={(e) => { e.stopPropagation(); remove(key); }}
						style={{ fontSize: 22, color: '#FF4D4F', cursor: 'pointer' }}
					/>
					<span style={{ flex: 1, fontSize: 15 }}>{titleOf(key)}</span>
					<HolderOutlined style={{ fontSize: 14, color: '#bbb' }} />
				</div>
			))}

			{candidates.length > 0 && (
				<>
					<div style={{
						padding: '14px 14px 6px',
						color: '#999',
						fontSize: 12,
						background: '#f0f0f0',
					}}>
						{t('rep.editor.available')}
					</div>
					{candidates.map((key) => (
						<div
							key={key}
							onClick={() => add(key)}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 14,
								padding: '14px 16px',
								background: '#fff',
								borderBottom: '1px solid #f5f5f5',
								cursor: 'pointer',
							}}
						>
							<PlusCircleOutlined style={{ fontSize: 22, color: '#52C41A' }} />
							<span style={{ flex: 1, fontSize: 15 }}>{titleOf(key)}</span>
						</div>
					))}
				</>
			)}
		</Drawer>
	);
}

/* ========== 顾客分析（本月，含环比） ========== */

/** iOS 复刻：每条卡片有「主用」code 与备用 code，
 *  主 code 无值时用备用 code 兜底。 */
const CUSTOMER_TYPES: {
	tags: string[];
	label_key: string;
	unit_key: string;
	percent: boolean;
}[] = [
	// 过店客流：face-based (101030) 优先，否则 head-count (102002)
	{ tags: ['101030', '102002'], label_key: 'rep.cust.gdrs',  unit_key: 'rep.unit.person', percent: false },
	// 进店客流：精准 (101001) 优先，否则 109001
	{ tags: ['101001', '109001'], label_key: 'rep.cust.jdkl',  unit_key: 'rep.unit.person', percent: false },
	// 进店转化率
	{ tags: ['101006'],           label_key: 'rep.cust.jdzhl', unit_key: 'rep.unit.percent', percent: true  },
	// 销售总额
	{ tags: ['101009'],           label_key: 'rep.cust.xsze',  unit_key: 'rep.unit.yuan',    percent: false },
];

function CustomerSection() {
	const { t, i18n } = useTranslation();
	const [items, setItems] = useState<TotalSummaryItem[] | null>(null);
	const [last,  setLast]  = useState<Map<string, string>>(new Map());

	useEffect(() => {
		const cur = thisMonth();
		const prev = lastMonthSameWindow();
		// 把每个 meta.tags 都展平交给后端
		const types = CUSTOMER_TYPES.reduce<string[]>((tags, c) => tags.concat(c.tags), []);
		Promise.all([
			fetchTotalSummary({ storeIds: '', startDate: cur.start,  endDate: cur.end,  types }),
			fetchTotalSummary({ storeIds: '', startDate: prev.start, endDate: prev.end, types }),
		]).then(([curr, prevArr]) => {
			setItems(curr);
			setLast(new Map(prevArr.map((it) => [it.type, it.value ?? '0'])));
		}).catch(() => setItems([]));
	}, []);

	const range = thisMonth();
	const subtitle = `${t('rep.cust.this_month')} (${formatDateRange(range.start, range.end, i18n.language)})`;

	return (
		<SectionCard
			icon={<UserOutlined style={{ color: '#FF4D4F' }} />}
			title={t('rep.cust.title')}
			subtitle={subtitle}
		>
			{items === null ? (
				<div style={{ padding: '20px 0', textAlign: 'center' }}><Spin /></div>
			) : (
				<div style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fill, minmax(min(110px, 100%), 1fr))',
					gap: 8,
				}}>
					{CUSTOMER_TYPES.map((meta) => {
						// 在 meta.tags 中按顺序找到第一个有 value 的 type
						const found = meta.tags
							.map((tag) => items.find((it) => it.type === tag))
							.find((it) => it && it.value != null && it.value !== '');
						const usedTag = found?.type;
						const value = found?.value;
						const prev = usedTag ? last.get(usedTag) : undefined;
						const display = value === undefined ? null
							: meta.percent ? (Number(value) * 100).toFixed(2)
							: formatNumber(Number(value), i18n.language);
						const diff = (() => {
							if (value === undefined || prev === undefined) return null;
							const c = Number(value), l = Number(prev);
							if (!Number.isFinite(l) || l <= 0) return null;
							const pct = ((c - l) * 100) / l;
							return { up: pct >= 0, pct: Math.abs(pct).toFixed(2) };
						})();
						return (
							<div key={meta.label_key} style={{
								background: '#fff', borderRadius: 10, padding: '12px 10px', minHeight: 84,
							}}>
								<div style={{ fontSize: 12, color: '#888' }}>{t(meta.label_key)}</div>
							<div style={{ marginTop: 4, fontSize: 16, fontWeight: 600, color: '#222', lineHeight: 1.1 }}>
									{display ?? '—'}
									<span style={{ fontSize: 11, color: '#888', fontWeight: 400, marginInlineStart: 2 }}>
										{t(meta.unit_key)}
									</span>
								</div>
								<div style={{ marginTop: 6, fontSize: 11, height: 14 }}>
									{diff ? (
										<span style={{ color: diff.up ? '#FF4D4F' : '#52C41A' }}>
											{diff.up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
											{' '}{diff.pct}%
										</span>
									) : <span style={{ color: '#bbb' }}>—</span>}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</SectionCard>
	);
}

/* ========== 悠巡（近 7 天） ========== */

interface YouxunItem {
	model_id: string;
	model_name: string;
	alarm_count: string;
	device_count: string;
	violation_count: string;
	type: string;
	// apac 返回时此字段可能缺失 / 为 null（cn 总返回对象）
	most_store?: { store_id: string; store_name: string } | null;
}

function YouxunSection() {
	const { t, i18n } = useTranslation();
	const [allItems, setAllItems] = useState<YouxunItem[] | null>(null);
	const [modules, setModules] = useState<HomeAppSection[] | null>(null);
	const [advancedOpen, setAdvancedOpen] = useState(false);
	const [refreshTick, setRefreshTick] = useState(0);

	// 拉所有可用模型（不传 model_ids 拿全集）
	useEffect(() => {
		const r = last7Days();
		callInternal<{ items: YouxunItem[] }>({
			short_name: 'standard',
			action: '/statistic/summary/youxun',
			query: { start_date: r.start, end_date: r.end, types: '103001,103002,103003' },
		}).then((res) => {
			if (res.code === 0 && res.data?.items) setAllItems(res.data.items);
			else setAllItems([]);
		}).catch(() => setAllItems([]));
	}, [refreshTick]);

	// 拉用户的 home_app_module 配置（找 tag=103 悠巡 的 algorithmModel）
	useEffect(() => {
		fetchHomeAppModule().then((mods) => setModules(mods ?? []))
			.catch(() => setModules([]));
	}, [refreshTick]);

	const youxunCfg = (modules ?? []).find((m) => m.tag === '103');
	const subscribed = youxunCfg?.algorithmModel ?? [];

	const range = last7Days();
	const subtitle = `${t('rep.last_7d')} (${formatDateRange(range.start, range.end, i18n.language)})`;

	// 渲染：若用户订阅了模型，按订阅顺序展示；否则按 alarm_count desc 取前 4
	const items = (() => {
		const all = allItems ?? [];
		if (subscribed.length > 0) {
			return subscribed
				.map((id) => all.find((it) => it.model_id === id))
				.filter((it): it is YouxunItem => !!it);
		}
		return [...all]
			.filter((it) => Number(it.alarm_count) > 0 || it.most_store?.store_name)
			.sort((a, b) => Number(b.alarm_count) - Number(a.alarm_count))
			.slice(0, 4);
	})();

	return (
		<SectionCard
			icon={<SafetyOutlined style={{ color: '#1890FF' }} />}
			title={t('rep.youxun.title')}
			subtitle={subtitle}
			extra={
				allItems && (
					<span
						onClick={() => setAdvancedOpen(true)}
						style={{ fontSize: 12, color: PRIMARY, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}
					>
						<SettingOutlined /> {t('rep.editor.advanced')}
					</span>
				)
			}
		>
			{allItems === null ? (
				<div style={{ padding: '20px 0', textAlign: 'center' }}><Spin /></div>
			) : items.length === 0 ? (
				<EmptyBlock />
			) : (
				<div>
					{items.map((it, i) => (
						<div key={it.model_id} style={{
							padding: '12px 12px',
							background: '#fff',
							borderTop: i === 0 ? 'none' : '1px solid #f5f5f5',
							borderRadius: i === 0 ? '10px 10px 0 0' : (i === items.length - 1 ? '0 0 10px 10px' : 0),
						}}>
							<div style={{ fontSize: 14, fontWeight: 500 }}>{it.model_name}</div>
							<div style={{ display: 'flex', marginTop: 8, gap: 12 }}>
								<MiniStat
									icon="🚨"
									label={t('rep.youxun.alarm_count')}
									value={`${formatNumber(Number(it.alarm_count), i18n.language)} ${t('rep.youxun.times')}`}
								/>
								<MiniStat
									icon="🏪"
									label={t('rep.youxun.most_store')}
									value={it.most_store?.store_name || t('rep.youxun.none')}
								/>
							</div>
						</div>
					))}
				</div>
			)}

			<YouxunAdvancedDrawer
				open={advancedOpen}
				onClose={() => setAdvancedOpen(false)}
				allItems={allItems ?? []}
				modules={modules ?? []}
				onSaved={() => {
					setAdvancedOpen(false);
					setRefreshTick((t) => t + 1);
				}}
			/>
		</SectionCard>
	);
}

/** 悠巡高级设置 — 选订阅哪些 AI 模型 */
function YouxunAdvancedDrawer({
	open,
	onClose,
	allItems,
	modules,
	onSaved,
}: {
	open: boolean;
	onClose: () => void;
	allItems: YouxunItem[];
	modules: HomeAppSection[];
	onSaved: () => void;
}) {
	const { t, i18n } = useTranslation();
	const [picked, setPicked] = useState<Set<string>>(new Set());
	const [search, setSearch] = useState('');
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open) return;
		const cfg = modules.find((m) => m.tag === '103');
		setPicked(new Set(cfg?.algorithmModel ?? []));
		setSearch('');
	}, [open, modules]);

	const visible = allItems.filter((it) => {
		if (!search) return true;
		const q = search.toLowerCase();
		return it.model_name.toLowerCase().includes(q) || it.model_id.includes(q);
	});

	const toggle = (id: string) => {
		setPicked((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id); else next.add(id);
			return next;
		});
	};

	const submit = async () => {
		setSaving(true);
		// 在 modules 中找到 tag=103 的 section，更新 algorithmModel
		const next = modules.map((m) =>
			m.tag === '103' ? { ...m, algorithmModel: [...picked] } : m,
		);
		// 若未来 modules 没有 tag=103 也补一个
		if (!next.some((m) => m.tag === '103')) {
			next.push({
				tag: '103',
				name: '悠巡',
				order: '4',
				algorithmModel: [...picked],
				sub: [],
			});
		}
		const r = await saveHomeAppModule(next);
		setSaving(false);
		if (r.ok) {
			antdMessage.success(t('rep.advanced.saved'));
			onSaved();
		} else {
			antdMessage.error(`${t('rep.advanced.save_failed')}: ${r.msg ?? ''}`);
		}
	};

	return (
		<Drawer
			placement="bottom"
			height="80%"
			open={open}
			onClose={onClose}
			closable={false}
			title={
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Button type="text" size="small" onClick={onClose} style={{ color: '#666' }}>
						{t('rep.editor.cancel')}
					</Button>
					<span style={{ flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 500 }}>
						{t('rep.advanced.title')}
					</span>
					<Button
						type="primary"
						size="small"
						loading={saving}
						onClick={submit}
						style={{ background: PRIMARY, borderColor: PRIMARY }}
					>
						{t('rep.editor.ok')}
					</Button>
				</div>
			}
			styles={{ body: { padding: 0, background: '#fafafa' }, header: { padding: '8px 16px' } }}
		>
			<div style={{ padding: '10px 12px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
				<Input.Search
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder={t('rep.advanced.search_ph')}
					allowClear
				/>
				<div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
					{t('rep.advanced.counter', { picked: picked.size, total: allItems.length })}
				</div>
			</div>
			{visible.map((it) => (
				<div
					key={it.model_id}
					onClick={() => toggle(it.model_id)}
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 12,
						padding: '14px 16px',
						background: '#fff',
						borderBottom: '1px solid #f5f5f5',
						cursor: 'pointer',
					}}
				>
					<Checkbox
						checked={picked.has(it.model_id)}
						onChange={() => toggle(it.model_id)}
						onClick={(e) => e.stopPropagation()}
					/>
					<div style={{ flex: 1, minWidth: 0 }}>
						<div style={{ fontSize: 14 }}>{it.model_name}</div>
						<div style={{ fontSize: 11, color: '#999' }}>
							ID {it.model_id} · {t('rep.youxun.alarm_count')} {formatNumber(Number(it.alarm_count), i18n.language)}
						</div>
					</div>
				</div>
			))}
			{visible.length === 0 && (
				<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('common.no_data')} style={{ marginTop: 50 }} />
			)}
		</Drawer>
	);
}

function MiniStat({ icon, label, value }: { icon: string; label: string; value: string }) {
	return (
		<div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
			<span style={{ fontSize: 18 }}>{icon}</span>
			<div style={{ flex: 1, minWidth: 0 }}>
				<div style={{ fontSize: 11, color: '#888' }}>{label}</div>
				<div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
			</div>
		</div>
	);
}

/* ========== 巡店通（今日） ========== */

interface XdtData {
	examine_submit_num?: number;
	examine_waiting_num?: number;
	examine_today_num?: number;
	inspection_submit_num?: number;
	inspection_waiting_num?: number;
	inspection_today_num?: number;
	dianjian_handle_num?: number;
	dianjian_waiting_num?: number;
	dianjian_expired_num?: number;
}

function XundiantongSection() {
	const { t, i18n } = useTranslation();
	const [data, setData] = useState<XdtData | null>(null);

	useEffect(() => {
		const day = today();
		callInternal<XdtData>({
			short_name: 'statgin',
			action: '/statistic/summary/xundiantong',
			// 后端校验用 PascalCase（Go validator tag），同时传 lowercase 兜底
			query: { StartDate: day, EndDate: day, start_date: day, end_date: day },
		}).then((res) => {
			if (res.code === 0) setData(res.data ?? {});
			else setData({});
		}).catch(() => setData({}));
	}, []);

	const subtitle = `${t('rep.today')} (${formatShortDate(today(), i18n.language)})`;

	const cards: { key: keyof XdtData; label_key: string }[] = [
		{ key: 'examine_submit_num',     label_key: 'rep.xdt.examine_submit'   },
		{ key: 'examine_waiting_num',    label_key: 'rep.xdt.examine_waiting'  },
		{ key: 'examine_today_num',      label_key: 'rep.xdt.examine_today'    },
		{ key: 'inspection_submit_num',  label_key: 'rep.xdt.inspect_submit'   },
		{ key: 'inspection_waiting_num', label_key: 'rep.xdt.inspect_waiting'  },
		{ key: 'inspection_today_num',   label_key: 'rep.xdt.inspect_today'    },
		{ key: 'dianjian_handle_num',    label_key: 'rep.xdt.dj_handled'       },
		{ key: 'dianjian_waiting_num',   label_key: 'rep.xdt.dj_waiting'       },
		{ key: 'dianjian_expired_num',   label_key: 'rep.xdt.dj_expired'       },
	];

	return (
		<SectionCard
			icon={<ShopOutlined style={{ color: '#52C41A' }} />}
			title={t('rep.xdt.title')}
			subtitle={subtitle}
		>
			{data === null ? (
				<div style={{ padding: '20px 0', textAlign: 'center' }}><Spin /></div>
			) : (
				<div style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fill, minmax(min(110px, 100%), 1fr))',
					gap: 8,
				}}>
					{cards.map((c) => (
						<div key={c.key} style={{
							background: '#fff', borderRadius: 10, padding: '14px 10px', textAlign: 'center',
						}}>
							<div style={{ fontSize: 12, color: '#666' }}>{t(c.label_key)}</div>
							<div style={{ marginTop: 6, fontSize: 20, fontWeight: 600, color: '#222' }}>
								{formatNumber(Number(data[c.key] ?? 0), i18n.language)}
								<span style={{ fontSize: 11, color: '#888', fontWeight: 400, marginInlineStart: 2 }}>
									{t('rep.xdt.unit')}
								</span>
							</div>
						</div>
					))}
				</div>
			)}
		</SectionCard>
	);
}

/* ========== 事件统计（近 7 天） ========== */

function EventSection() {
	const { t, i18n } = useTranslation();
	// apac 实测：important_event/newest_event 字段可能返回 `{}` 而非 string —— 用 unknown 收，渲染前再判型
	const [data, setData] = useState<{ event_total?: string; waiting_handle_num?: string; important_event?: unknown } | null>(null);

	useEffect(() => {
		const r = last7Days();
		callInternal<{ event_total?: string; waiting_handle_num?: string; important_event?: unknown }>({
			short_name: 'standard',
			action: '/statistic/summary/event',
			query: { start_date: r.start, end_date: r.end },
		}).then((res) => setData(res.code === 0 ? (res.data ?? {}) : {}))
		  .catch(() => setData({}));
	}, []);

	const range = last7Days();
	const subtitle = `${t('rep.last_7d')} (${formatDateRange(range.start, range.end, i18n.language)})`;

	return (
		<SectionCard
			icon={<BellOutlined style={{ color: '#FAAD14' }} />}
			title={t('rep.event.title')}
			subtitle={subtitle}
		>
			{data === null ? (
				<div style={{ padding: '20px 0', textAlign: 'center' }}><Spin /></div>
			) : Object.keys(data).length === 0 ? (
				<EmptyBlock />
			) : (
				<div style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fill, minmax(min(110px, 100%), 1fr))',
					gap: 8,
				}}>
					<EventCard label={t('rep.event.total')}    value={data.event_total ?? '0'} unit={t('rep.event.unit')} />
					<EventCard label={t('rep.event.pending')}  value={data.waiting_handle_num ?? '0'} unit={t('rep.event.unit')} />
					<EventCard label={t('rep.event.key')}      value={typeof data.important_event === 'string' && data.important_event ? data.important_event : t('rep.event.none')} />
				</div>
			)}
		</SectionCard>
	);
}

function EventCard({ label, value, unit }: { label: string; value: string; unit?: string }) {
	const { i18n } = useTranslation();
	const numeric = unit && !Number.isNaN(Number(value));
	return (
		<div style={{
			background: '#fff', borderRadius: 10, padding: '14px 10px', textAlign: 'center',
		}}>
			<div style={{ fontSize: 12, color: '#666' }}>{label}</div>
			<div style={{ marginTop: 6, fontSize: 16, fontWeight: 600, color: '#222' }}>
				{numeric ? formatNumber(Number(value), i18n.language) : value}
				{numeric && unit && (
					<span style={{ fontSize: 11, color: '#888', fontWeight: 400, marginInlineStart: 2 }}>
						{unit}
					</span>
				)}
			</div>
		</div>
	);
}

/* ========== 公共 ========== */

function SectionCard({
	icon,
	title,
	subtitle,
	extra,
	children,
}: {
	icon: React.ReactNode;
	title: string;
	subtitle: string;
	extra?: React.ReactNode;
	children: React.ReactNode;
}) {
	return (
		<div style={{ marginBottom: 14 }}>
			<div style={{
				display: 'flex',
				alignItems: 'baseline',
				gap: 6,
				marginBottom: 8,
				padding: '0 4px',
			}}>
				<span style={{ fontSize: 16 }}>{icon}</span>
				<span style={{ fontSize: 16, fontWeight: 600 }}>{title}</span>
				<span style={{ fontSize: 12, color: '#999', flex: 1 }}>{subtitle}</span>
				{extra}
			</div>
			<div>{children}</div>
		</div>
	);
}

function EmptyBlock() {
	const { t } = useTranslation();
	return (
		<div style={{
			background: '#fff',
			borderRadius: 10,
			padding: '24px 0',
			textAlign: 'center',
		}}>
			<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('rep.no_content')} />
		</div>
	);
}
